import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';

import { ToastContext } from '@/components/common/CommonToast';
import { useLoading } from '@/components/common/loader/LoadingContext';
import Spinner from '@/components/common/loader/Spinner';
import ButtonCustom from '@/components/common/ui/button';
import { CalendarCustomForm } from '@/components/common/ui/calendar';
import { useTranslation } from '@/config/i18n';
import useScrollToError from '@/hooks/useScrollToError';
import { toastErrorMessage, toastMessage } from '@/lib/utils';
import PATH from '@/routes/path';
import { apiRequest } from '@/services';
import { HTTP_STATUS } from '@/shared/constants';
import {
  graduationFormSchema,
  GraduationFormType,
} from '@/shared/schema/graduation';
import InfinitySelect from '@/components/common/ui/select';
import DialogCustom from '@/components/common/ui/dialog';
import { GraduationTermLessonType, GraduationType } from '@/types/graduation';
import {
  COURSE_PERMISSION_ERROR_MESSAGE_KEY,
  graduationStatus,
} from '@/shared/constants/graduation';
import {
  duePaymentDateFromApi,
  trialEndMaxDateForDuePayment,
} from '@/shared/graduation/billingMonth';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import useCoursePermissionSync from '@/pages/graduation-management/hooks/useCoursePermissionSync';
import GraduationTermLessons, {
  useGraduationLessons,
} from '@/pages/graduation-management/components/GraduationTermLessons';

type Props = {
  id?: number;
};

const GraduationForm: React.FC<Props> = ({ id }) => {
  const { toast } = useContext(ToastContext);
  const [t] = useTranslation('');
  const { apiService } = apiRequest();

  const navigate = useNavigate();
  const { scrollToError } = useScrollToError();
  const { loadingGlobal, setLoadingGlobal } = useLoading();
  const [dialogConfirmEditVisible, setDialogConfirmEditVisible] =
    useState(false);
  const [dataEdit, setDataEdit] = useState<GraduationType>();
  const [graduationDetailStatus, setGraduationDetailStatus] =
    useState<string>();
  const [duePublicDateInitial, setDuePublicDateInitial] = useState<string>('');

  const formMethods = useForm<GraduationFormType>({
    mode: 'onChange',
    resolver: yupResolver(graduationFormSchema),
    shouldFocusError: false,
    defaultValues: {
      learning_start_date: undefined,
      due_public_date: undefined,
      terms: [
        {
          name: '',
          trial_end_date: undefined,
          due_payment_date: undefined,
          lessons: [],
        },
      ],
      deleted_term_ids: [],
    },
  });

  const {
    handleSubmit,
    clearErrors,
    formState: { errors },
    setValue,
    getValues,
    watch,
    control,
  } = formMethods;

  const {
    fields: termFields,
    append: appendTerm,
    remove: removeTerm,
  } = useFieldArray({
    control,
    name: 'terms',
  });

  // Shared across every term item so the list is fetched once for the whole form.
  const graduationLessons = useGraduationLessons();

  const { isSyncing, startWatching, applyDetailStatus } = useCoursePermissionSync({
    graduationId: id,
    onDone: ({ startedBySave }) => {
      // A save is only finished once the permissions it triggered are applied, so the trip
      // back to the list waits for that instead of firing at the PUT - which is also what
      // leaves the form mounted long enough to be locked while the job runs.
      if (startedBySave) {
        handleNavigateBack();
        return;
      }

      // Nothing was asked of this screen: the run was already in flight when it opened, so
      // it just picks up what the job wrote and stays put.
      if (id) getGraduationDetail(id);
    },
  });

  const graduationOptions = useMemo(() => {
    return [
      ...Array.from({ length: 999 }, (_, i) => ({
        value: i + 1,
        label: `${i + 1}`,
      })),
    ];
  }, []);

  const termsWatch = watch('terms');
  const duePublicDateWatched = watch('due_public_date');

  const earliestTrialEnd = useMemo(() => {
    if (!termsWatch || !Array.isArray(termsWatch)) {
      return undefined;
    }

    const validDates: Date[] = termsWatch
      .map((t: any) => t?.trial_end_date)
      .filter((d: any) => d && dayjs(d).isValid())
      .map((d: any) => dayjs(d).toDate());

    if (validDates.length === 0) {
      return undefined;
    }

    const minDate = validDates.reduce((min, current) =>
      current.getTime() < min.getTime() ? current : min,
    );

    return minDate;
  }, [termsWatch]);

  const getAllFieldNames = () => {
    const values = getValues();
    return Object.keys(values);
  };

  function setDefaultValue(data: any) {
    const specialField = ['learning_start_date', 'period_number', 'due_public_date'];
    getAllFieldNames().forEach((field: any) => {
      const val = data[field];

      if (val && !specialField.includes(field)) {
        setValue(field, val);
      }

      if (field === 'period_number') {
        setValue(field, {
          label: val,
          value: val,
        });
      }

      if (field === 'due_public_date' || field === 'learning_start_date') {
        if (!val || val === '') return setValue(field, undefined);

        setValue(field, new Date(val));
      }
    });
  }

  async function getGraduationDetail(id: number) {
    setLoadingGlobal(true);
    const { data: graduation } = await apiService.graduations.show(id);

    if (graduation?.data) {
      setDefaultValue(graduation.data);
      setGraduationDetailStatus(graduation.data.status);
      setDuePublicDateInitial(graduation.data.due_public_date);
      applyDetailStatus(graduation.data.permission_sync_status);

      if (graduation.data.terms && Array.isArray(graduation.data.terms)) {
        // The detail response carries the grid under terms[].lessons, so no extra request is
        // needed - and a failed load can no longer be mistaken for "this term has no settings".
        const mappedTerms = graduation.data.terms.map((term: any) => ({
          id: term.id,
          name: term.name ?? '',
          trial_end_date: term.trial_end_date
            ? new Date(term.trial_end_date)
            : undefined,
          due_payment_date: duePaymentDateFromApi(term.due_payment_date),
          lessons: term.lessons ?? [],
        }));

        setValue('terms', mappedTerms.length > 0 ? mappedTerms : [
          {
            name: '',
            trial_end_date: undefined,
            due_payment_date: undefined,
            lessons: [],
          },
        ]);
      }
    }
    setLoadingGlobal(false);
  }

  function getPayload(data: GraduationFormType) {
    // dayjs(undefined) is valid and defaults to "now" — guard null/undefined explicitly.
    const formatDayMonthYear = (date: Date | null | undefined) => {
      if (date === undefined || date === null) return '';
      return dayjs(date).isValid() ? dayjs(date).format('YYYY/MM/DD') : '';
    };

    const formatMonthYear = (date: Date | null | undefined) => {
      if (date === undefined || date === null) return '';
      return dayjs(date).isValid() ? dayjs(date).format('YYYY/MM') : '';
    };

    const deletedIds = new Set<number>(
      (data.deleted_term_ids ?? []).filter(
        (id): id is number => typeof id === 'number',
      ),
    );

    const terms =
      data.terms
        ?.filter((term: any) => !(term.id && deletedIds.has(term.id)))
        .map((term: any) => ({
          ...(term.id ? { id: term.id } : {}),
          name: term.name,
          trial_end_date: formatDayMonthYear(term.trial_end_date) || null,
          due_payment_date: formatMonthYear(term.due_payment_date),
          // The ART SHIFT content settings travel with the term: one save is one request and
          // one transaction, and a brand new term gets its grid without a second round trip.
          lessons: buildTermLessons(term.lessons ?? []),
        })) ?? [];

    return {
      period_number: data.period_number,
      learning_start_date: formatDayMonthYear(data.learning_start_date),
      due_public_date: formatDayMonthYear(data.due_public_date),
      terms,
      deleted_term_ids: data.deleted_term_ids ?? [],
    };
  }

  function handleShowDialogConfirmEdit() {
    const nextDialogVisibleValue = !dialogConfirmEditVisible;
    setDialogConfirmEditVisible(nextDialogVisibleValue);

    if (nextDialogVisibleValue) return;
  }

  async function onSaveGraduation(data: GraduationFormType) {
    clearErrors();

    const payload = getPayload(data);

    if (id) {
      handleShowDialogConfirmEdit();
      setDataEdit(payload as GraduationType);

      return;
    }

    await handleSaveCreateGraduation(payload);
  }

  function handleNavigateBack() {
    navigate(PATH.graduation);
  }

  function handleError(error: any) {
    const { status_code, errors, message } = error;
    if (status_code === HTTP_STATUS.FORM_ERROR) {
      (errors ?? []).forEach((e: any) => {
        const { field, error_code: errorCode } = e;
        if (field === 'period_number') {
          toast?.current?.show(
            toastErrorMessage(t('graduation.graduation_name_already_exists')),
          );
        }

        if (field === 'due_public_date') {
          toast?.current?.show(
            toastErrorMessage(t('graduation.due_public_date_error')),
          );
        }

        // Course permission rules ship an error code only - the wording is owned here.
        const lessonMessageKey = COURSE_PERMISSION_ERROR_MESSAGE_KEY[errorCode];
        if (lessonMessageKey) {
          toast?.current?.show(toastErrorMessage(t(lessonMessageKey)));
        }
      });
      return;
    }

    const msg = message;
    toast?.current?.show(toastErrorMessage(msg));
  }

  /**
   * Builds `terms[].lessons` of the graduation payload.
   *
   * The form only holds the rows the admin actually touched, but the BE deletes every row
   * missing from the payload. Sending that sparse state would drop the untouched lessons
   * instead of storing them as OFF - which is what records the explicit denial and marks
   * the term as configured. So the gaps are filled from the admin lesson list.
   */
  function buildTermLessons(
    rows: GraduationTermLessonType[],
  ): GraduationTermLessonType[] {
    const touched = new Map(rows.map((row) => [row.lesson_id, row]));

    return graduationLessons.lessons.map(
      (lesson) =>
        touched.get(lesson.id) ?? {
          lesson_id: lesson.id,
          is_available: false,
          unit_limit: null,
          is_shared: false,
        },
    );
  }

  /**
   * The full admin lesson list is required to submit the full grid: saving while it is
   * missing or still loading would send an empty grid and wipe the stored settings.
   */
  function checkLessonsLoaded() {
    if (graduationLessons.failed) {
      toast?.current?.show(
        toastErrorMessage(t('graduation.lesson_load_failed')),
      );
      return false;
    }

    if (!graduationLessons.loaded) {
      toast?.current?.show(toastErrorMessage(t('graduation.lesson_not_ready')));
      return false;
    }

    return true;
  }

  async function handleSaveEditGraduation(
    payload?: GraduationType,
    id?: number,
  ) {
    if (!id) return;

    if (!checkDuePublicDate()) return;

    if (!checkLessonsLoaded()) return;

    setLoadingGlobal(true);
    // Single request: the dates, the terms and their content settings are one transaction on the
    // BE, so a rejected lesson can no longer leave part of the form saved.
    const { error: graduationError } = await apiService.graduations.update(id, payload);

    if (graduationError) {
      handleError(graduationError);
      // Reload latest data from API to sync form state after failed update
      await getGraduationDetail(id);
      setLoadingGlobal(false);
      setDialogConfirmEditVisible(false);
      return;
    }

    setLoadingGlobal(false);
    setDialogConfirmEditVisible(false);
    toast?.current?.show(toastMessage(t('graduation.edit_success')));
    startWatching();
  }

  function checkDuePublicDate() {
    const duePublicDate = dayjs(getValues('due_public_date'));
    const today = dayjs();
    if (duePublicDate.isBefore(today, 'day') && checkDuePublicDateChanged()) {
      toast?.current?.show(
        toastErrorMessage(t('graduation.required.recruitment_start_date')),
      );
      return false;
    }
    return true;
  }

  function checkDuePublicDateChanged() {
    const duePublicDateInput = dayjs(getValues('due_public_date'));
    const duePublicDateInitialInput = dayjs(duePublicDateInitial);
    
    return !duePublicDateInput.isSame(duePublicDateInitialInput, 'day');
  }

  async function handleSaveCreateGraduation(payload: any) {
    if (!checkDuePublicDate()) return;

    if (!checkLessonsLoaded()) return;

    setLoadingGlobal(true);
    // Single request, single transaction: a rejected lesson leaves no graduation behind, so the
    // admin can fix the grid and submit again instead of colliding with unique period_number.
    const { error: graduationError } = await apiService.graduations.create(payload);

    if (graduationError) {
      handleError(graduationError);
      setLoadingGlobal(false);
      setDialogConfirmEditVisible(false);
      return;
    }

    toast?.current?.show(toastMessage(t('graduation.save_success')));
    handleNavigateBack();
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      scrollToError();
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [errors]);

  useEffect(() => {
    if (id) {
      getGraduationDetail(id);
    }
  }, [id]);

  const isClosedGraduation =
    !!id && graduationDetailStatus === graduationStatus.CLOSED;

  return (
    <>
      <FormProvider {...formMethods}>
        <form
          className="mt-[16px] relative"
          autoComplete="off"
          onSubmit={handleSubmit(onSaveGraduation)}
        >
          <fieldset
            disabled={isSyncing}
            className="m-0 min-w-0 border-0 p-0"
            data-testid="graduation-form-fieldset"
          >
            <div className="form-item flex">
              <div className="label w-56 flex">
                <span>{t('graduation.period_number')}</span>
                <span className="text-error-500">*</span>
              </div>
              <InfinitySelect
                placeholder={t('graduation.period_number_placeholder')}
                wrapClass="w-[512px]"
                name="period_number"
                defaultOption={graduationOptions}
                focusDefaultOption={false}
                onMenuOpen={() => {
                  setTimeout(() => {
                    const selectedEl = document.getElementsByClassName(
                      'select__option--is-selected',
                    )[0];
                    if (selectedEl) {
                      selectedEl.scrollIntoView({});
                    }
                  }, 15);
                }}
                customNoOptionsMessage={t('graduation.period_number_no_data')}
                isDisabled={isClosedGraduation}
              />
            </div>

            <div className="form-item flex mt-[20px]">
              <div className="label w-56 flex">
                <span>{t('graduation.learning_start_date')}</span>
                <span className="text-error-500">*</span>
              </div>
              <div className="w-[512px]">
                <CalendarCustomForm
                  wrapClass="w-full"
                  name="learning_start_date"
                  dateFormat="yy/mm/dd"
                  showIcon
                  icon={() => <i className="pi pi-calendar text-[18px]" />}
                  placeholder="yyyy/mm/dd"
                  panelClassName="calendar-date-of-birth"
                  showButtonBar
                  disabled={isClosedGraduation}
                />
              </div>
            </div>

            <div className="form-item flex mt-[20px]">
              <div className="label w-56 flex">
                <span>{t('graduation.recruitment_start_date')}</span>
                <span className="text-error-500">*</span>
              </div>
              <div className="w-[512px]">
                <CalendarCustomForm
                  wrapClass="w-full"
                  name="due_public_date"
                  dateFormat="yy/mm/dd"
                  showIcon
                  icon={() => <i className="pi pi-calendar text-[18px]" />}
                  placeholder="yyyy/mm/dd"
                  panelClassName="calendar-date-of-birth"
                  minDate={dayjs().add(1, 'day').toDate()}
                  maxDate={
                    earliestTrialEnd
                      ? dayjs(earliestTrialEnd).subtract(1, 'day').toDate()
                      : undefined
                  }
                  showButtonBar
                  disabled={isClosedGraduation}
                />
              </div>
            </div>

            <div className="mt-[32px]">
              <div className="form-item flex">
                <div className="label w-56 flex">
                  <span>{t('graduation.course_group_name')}</span>
                </div>
              </div>

              <div className="flex flex-col gap-4 mt-[12px]">
                {termFields.map((field, index) => {
                  const termRow = termsWatch?.[index];
                  const duePublicDateValue = duePublicDateWatched;
                  const trialEndValue = termRow?.trial_end_date;
                  const duePaymentValue = termRow?.due_payment_date;
                  const termNameValue =
                    (termRow?.name as string | undefined) ||
                    getValues(`terms.${index}.name`) ||
                    '';

                  const trialMinDate = dayjs(
                    duePublicDateValue || dayjs().add(1, 'day').toDate(),
                  )
                    .add(1, 'day')
                    .toDate();

                  const trialMaxDate =
                    trialEndMaxDateForDuePayment(duePaymentValue);

                  const paymentMinDate = (() => {
                    if (!trialEndValue) {
                      return dayjs().add(1, 'month').startOf('month').toDate();
                    }
                    const t = dayjs(trialEndValue).startOf('day');
                    const monthStart = t.startOf('month');
                    return (
                      monthStart.isBefore(t)
                        ? t.add(1, 'month').startOf('month')
                        : monthStart
                    ).toDate();
                  })();

                  return (
                    <div
                      key={field.id}
                      className="border border-gray-200 rounded-lg p-4 flex flex-col gap-2"
                    >
                      <div className="flex gap-6 items-start">
                        <div className="flex-1 min-w-[260px]">
                          <div className="mb-1 text-[14px] text-gray-900">
                            <span>{t('graduation.group_name') || 'グループ名'}</span>
                            <span className="text-error-500 ml-1">*</span>
                          </div>
                          {isClosedGraduation ? (
                            <div className="h-[43px] border border-gray-300 bg-[#f2f2f2] rounded-lg opacity-70 py-[10px] px-[12px]">
                              <Tooltip target={`.tooltip-point-graduation-term-${index}`}>
                                {termNameValue}
                              </Tooltip>
                              <div
                                className={`line-clamp-1 text-gray-700 break-all tooltip-point-graduation-term-${index}`}
                                data-pr-position="top"
                              >
                                {termNameValue}
                              </div>
                            </div>
                          ) : (
                            <InputTextCustomForm
                              name={`terms.${index}.name`}
                              placeholder={
                                t('graduation.group_name_placeholder') ||
                                '入力してください。'
                              }
                              showErrorWhileFocused
                              requiredFlag
                              wrapClass="w-full"
                            />
                          )}
                        </div>

                        <div className="flex-1 min-w-[260px]">
                          <div className="mb-1 text-[14px] text-gray-900">
                            <span>
                              {t('graduation.coach_authority_available')}
                            </span>
                          </div>
                          <CalendarCustomForm
                            wrapClass="w-full"
                            name={`terms.${index}.trial_end_date`}
                            dateFormat="yy/mm/dd"
                            showIcon
                            icon={() => (
                              <i className="pi pi-calendar text-[18px]" />
                            )}
                            placeholder="yyyy/mm/dd"
                            panelClassName="calendar-date-of-birth"
                            minDate={trialMinDate}
                            maxDate={trialMaxDate}
                            showButtonBar
                            disabled={isClosedGraduation}
                          />
                        </div>

                        <div className="flex-1 min-w-[260px]">
                          <div className="mb-1 text-[14px] text-gray-900">
                            <span>{t('graduation.start_date_charging')}</span>
                            <span className="text-error-500 ml-1">*</span>
                          </div>
                          <CalendarCustomForm
                            wrapClass="w-full"
                            name={`terms.${index}.due_payment_date`}
                            dateFormat="yy/mm"
                            showIcon
                            icon={() => (
                              <i className="pi pi-calendar text-[18px]" />
                            )}
                            placeholder="yyyy/mm"
                            panelClassName="calendar-date-of-birth"
                            minDate={paymentMinDate}
                            showButtonBar
                            view="month"
                            disabled={isClosedGraduation}
                          />
                        </div>

                        <div className="w-[56px] flex self-stretch items-center justify-center">
                          {index > 0 && !isClosedGraduation ? (
                            <button
                              type="button"
                              className="text-error-500 text-sm whitespace-nowrap"
                              onClick={() => {
                                const currentTerms = getValues('terms') as any[];
                                const term = currentTerms[index];
                                if (term?.id) {
                                  const currentDeleted = getValues('deleted_term_ids') ?? [];
                                  setValue('deleted_term_ids', [...currentDeleted, term.id], {
                                    shouldValidate: false,
                                    shouldDirty: true,
                                  });
                                }
                                removeTerm(index);
                              }}
                            >
                              削除
                            </button>
                          ) : (
                            <span className="inline-block text-sm opacity-0">
                              削除
                            </span>
                          )}
                        </div>
                      </div>

                      <GraduationTermLessons
                        termIndex={index}
                        {...graduationLessons}
                      />
                    </div>
                  );
                })}

                {!isClosedGraduation ? (
                  <button
                    type="button"
                    disabled={termFields.length >= 5}
                    className={`text-sm font-medium w-fit ${
                      termFields.length >= 5
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-primary-600'
                    }`}
                    onClick={() => {
                      if (termFields.length >= 5) {
                        return;
                      }
                      appendTerm({
                        name: '',
                        lessons: [],
                      } as any);
                    }}
                  >
                    + {t('graduation.add_group') || 'グループを追加'}
                  </button>
                ) : null}
              </div>
            </div>

            <div className="action flex justify-end mt-[25px] border-t border-gray-200 py-[18px] gap-5">
              <ButtonCustom
                className="w-[140px]"
                type="button"
                severity="secondary"
                onClick={() => handleNavigateBack()}
              >
                {t('graduation.cancel_button')}
              </ButtonCustom>
              <ButtonCustom
                className="w-[140px]"
                type="submit"
                loading={loadingGlobal}
              >
                {t('graduation.save_button')}
              </ButtonCustom>
            </div>
          </fieldset>

          {isSyncing && (
            <div
              className="absolute inset-0 z-10 bg-white/60"
              data-testid="graduation-form-scrim"
            >
              <div className="sticky top-1/2 flex flex-col items-center gap-3 text-gray-900">
                <Spinner />
                <span>{t('graduation.permission_sync_running')}</span>
              </div>
            </div>
          )}
        </form>
      </FormProvider>

      <DialogCustom
        header={t('graduation.save_confirm.title')}
        visible={dialogConfirmEditVisible}
        className="w-[480px]"
        headerClassName="border-b border-gray-200 !py-[13px]"
        contentClassName="border-b border-gray-200 !p-[16px] !text-[16px] !leading-[24px] text-center text-gray-900 whitespace-pre-line"
        onHide={() => setDialogConfirmEditVisible(false)}
        footer={
          <div className="flex gap-4 justify-end">
            <ButtonCustom
              type="button"
              className="!rounded-md"
              severity="secondary"
              onClick={() => setDialogConfirmEditVisible(false)}
            >
              {t('graduation.save_confirm.cancel')}
            </ButtonCustom>
            <ButtonCustom
              type="button"
              className="!rounded-md"
              loading={loadingGlobal}
              onClick={async () => await handleSaveEditGraduation(dataEdit, id)}
            >
              {t('graduation.save_confirm.save')}
            </ButtonCustom>
          </div>
        }
      >
        {t('graduation.save_confirm.content')}
      </DialogCustom>
    </>
  );
};

export default GraduationForm;
